import api from './api';
import type { OnboardingPhase1Step, OnboardingPhase2Step } from '@tcr-erp/shared';
/**
 * Service cho Onboarding flow (EG-356 Sprint 2). 7 endpoint:
 *   - GET  /onboarding/progress
 *   - PATCH /tenants/me                    (step 1 — business)
 *   - POST  /stores                        (step 2 — store)
 *   - POST  /staff/invite                  (step 3 — staff invite)
 *   - POST  /products/import + seed-sample (step 4 — products)
 *   - PATCH /payment/config                (step 5 — payment)
 *   - PUT   /e-invoice-config              (step 6 — e-invoice; single resource per tenant)
 *   - POST  /onboarding/complete           (finalize)
 *
 * BE wrap response `{ data: {...}, success: true }` — service unwrap về flat shape.
 */

function unwrap<T = Record<string, unknown>>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    const inner = (raw as { data: unknown }).data;
    if (inner && typeof inner === 'object') return inner as T;
  }
  return (raw ?? {}) as T;
}

// ============================================================
// TYPES
// ============================================================

// ADR-013: API JSON dùng snake_case end-to-end. Type mirror đúng response BE
// (xem tcr-erp-be-tenant onboarding.controller.ts toResponse).
export interface OnboardingProgress {
  tenant_id: string;
  tenant_name: string;
  address: string;
  tax_code?: string;
  phone?: string;
  email?: string;
  logo?: string;
  city_id: string;
  ward_id: string;
  phase_1: {
    total_steps: number;
    completed_steps: OnboardingPhase1Step[];
    skipped_steps: OnboardingPhase1Step[];
    is_completed: boolean;
    completed_at: string | null;
  };
  phase_2: {
    total_steps: number;
    completed_steps: OnboardingPhase2Step[];
    skipped_steps: OnboardingPhase2Step[];
    is_completed: boolean;
    completed_at: string | null;
    dismissed_at: string | null;
  };
}

export interface BusinessInfoPayload {
  name?: string;
  taxCode?: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
  cityId?: string;
  wardId?: string;
}

/** EG-356 Step 2 — POST /stores body. BE DTO `CreateStoreDto`
 *  ([tcr-erp-be-tenant/src/modules/store/dto/create-store.dto.ts]) snake_case
 *  end-to-end (ADR-013 §D2). Caller (store.tsx) build trực tiếp shape này
 *  từ Antd form values. */
export interface StorePayload {
  store_id?: string;
  name: string;
  address: string;
  phone?: string;
  open_time?: string;
  close_time?: string;
  working_days?: string;
  city_id?: string;
  ward_id?: string;
  hardware?: string[];
}

export interface StaffInvitePayload {
  email?: string;
  fullName: string;
  phone?: string;
  role:
    | 'manager'
    | 'inventory'
    | 'truong_nhom_kd'
    | 'gd_kd'
    | 'ecom_sales_manager'
    | 'nv_sale_ecom'
    | 'ke_toan_truong'
    | 'nv_kho'
    | 'shipper'
    | 'gd_hr'
    | 'nv_hr'
    | 'nv_mua_hang';
  storeIds?: string[];
  inviteMethod?: 'email' | 'sms';
}

export interface ProductRow {
  name: string;
  sku?: string;
  description?: string;
  categoryName?: string;
  price?: number;
  stock?: number;
}

export interface PaymentConfigPayload {
  paymentMethods: string[];
  bankAccounts?: Array<{
    id?: string;
    accountNumber: string;
    accountName: string;
    bankCode: string;
    bankName?: string;
  }>;
  shifts?: Array<{ name: string; startTime: string; endTime: string }>;
  requireShiftOpen?: boolean;
}

export interface EInvoiceConfigPayload {
  provider: 'meinvoice' | 'vnpt' | 'viettel' | 'skip';
  taxCode?: string;
  companyName?: string;
  address?: string;
  isRegisteredWithTaxDept?: boolean;
  apiKey?: string;
  templateCode?: string;
}

export interface OnboardingStoreRecord {
  _id: string;
  name: string;
  address: string;
  phone?: string;
  open_time?: string;
  close_time?: string;
}

// ============================================================
// SERVICE
// ============================================================

export const onboardingService = {
  /** Hydrate state lúc mount Onboarding layout. */
  async getProgress(): Promise<OnboardingProgress> {
    const { data: raw } = await api.get('/onboarding/progress');
    return unwrap<OnboardingProgress>(raw) as OnboardingProgress;
  },

  /** Mark 1 step done (Phase 1). */
  async markStepComplete(stepId: OnboardingPhase1Step): Promise<OnboardingProgress> {
    const { data: raw } = await api.put(`/onboarding/phase1/steps/${stepId}`);
    return unwrap<OnboardingProgress>(raw) as OnboardingProgress;
  },

  /** Mark 1 step skipped. */
  async skipStep(stepId: OnboardingPhase1Step): Promise<OnboardingProgress> {
    const { data: raw } = await api.post(`/onboarding/phase1/skip/${stepId}`);
    return unwrap<OnboardingProgress>(raw) as OnboardingProgress;
  },

  /** Mark 1 step done (Phase 2 ready-to-sell). */
  async markPhase2StepComplete(stepId: OnboardingPhase2Step): Promise<OnboardingProgress> {
    const { data: raw } = await api.put(`/onboarding/phase2/steps/${stepId}`);
    return unwrap<OnboardingProgress>(raw) as OnboardingProgress;
  },

  /** Mark 1 Phase 2 step skipped. */
  async skipPhase2Step(stepId: OnboardingPhase2Step): Promise<OnboardingProgress> {
    const { data: raw } = await api.post(`/onboarding/phase2/skip/${stepId}`);
    return unwrap<OnboardingProgress>(raw) as OnboardingProgress;
  },

  /** Dismiss Phase 2 checklist — ẩn vĩnh viễn khỏi dashboard. */
  async dismissPhase2(): Promise<void> {
    await api.post('/onboarding/phase2/dismiss');
  },

  /** Finalize onboarding — mở khoá dashboard. */
  async complete(): Promise<{ redirect_url: string } & OnboardingProgress> {
    const { data: raw } = await api.post('/onboarding/complete');
    return unwrap(raw) as { redirect_url: string } & OnboardingProgress;
  },

  // ---- Step 1: Business info ----
  async updateBusinessInfo(payload: BusinessInfoPayload) {
    const { data: raw } = await api.patch('/tenants/me', payload);
    return unwrap(raw);
  },

  // ---- Step 2: Store ----
  async createStore(payload: StorePayload) {
    const { store_id: _storeId, hardware: _hardware, ...body } = payload;
    const { data: raw } = await api.post('/stores', body);
    return unwrap(raw);
  },

  async updateStore(storeId: string, payload: Partial<StorePayload>) {
    const { store_id: _storeId, hardware: _hardware, ...body } = payload;
    const { data: raw } = await api.patch(`/stores/${storeId}`, body);
    return unwrap(raw);
  },

  async listStores(): Promise<OnboardingStoreRecord[]> {
    const { data: raw } = await api.get('/stores', { params: { limit: 200 } });
    const body = unwrap<{ data?: unknown[] } | unknown[]>(raw);
    const arr = Array.isArray(body)
      ? body
      : Array.isArray((body as { data?: unknown[] }).data)
        ? (body as { data: unknown[] }).data
        : [];
    return arr.map((item) => {
      const row = item as Record<string, unknown>;
      const opening = row.opening_hours as { open?: unknown; close?: unknown } | undefined;
      return {
        _id: String(row._id ?? row.id ?? ''),
        name: String(row.name ?? ''),
        address: String(row.address ?? ''),
        phone: typeof row.phone === 'string' ? row.phone : undefined,
        open_time: typeof opening?.open === 'string' ? opening.open : undefined,
        close_time: typeof opening?.close === 'string' ? opening.close : undefined,
      };
    });
  },

  // ---- Step 3: Staff invite ----
  async inviteStaff(payload: StaffInvitePayload) {
    const { data: raw } = await api.post('/staff/invite', payload);
    return unwrap(raw);
  },

  // ---- Step 4: Products ----
  /**
   * ⚠️ TODO(EG-356 follow-up): BE chưa implement `POST /products/import`.
   * Sẽ 404. UI nên disable luồng CSV cho đến khi BE ready, hoặc throw
   * có thông báo rõ ràng. Caller (products.tsx) hiện vẫn có flow `idle`
   * (skip không call API) là path ổn định.
   */
  async importProducts(products: ProductRow[], dryRun = false) {
    const { data: raw } = await api.post('/products/import', {
      products,
      dryRun,
    });
    return unwrap(raw);
  },

  /**
   * BE endpoint thực: `POST /onboarding/seed-sample-data` (route mounted
   * dưới OnboardingController, KHÔNG phải `/products/seed-sample`).
   * Tạo 20 sản phẩm + 5 khách hàng + 3 POS order mẫu, idempotent qua
   * `is_sample_data=true` flag.
   */
  async seedSampleProducts() {
    const { data: raw } = await api.post('/onboarding/seed-sample-data');
    return unwrap(raw);
  },

  async deleteSampleData(): Promise<{ deleted: { products: number; variants: number; customers: number; orders: number } }> {
    const { data: raw } = await api.delete('/onboarding/sample-data');
    return unwrap(raw) as any;
  },

  // ---- Step 5: Payment ----
  async updatePaymentConfig(payload: PaymentConfigPayload) {
    const { data: raw } = await api.patch('/payment/config', payload);
    return unwrap(raw);
  },

  // ---- Step 6: E-Invoice ----
  /**
   * ⚠️ KNOWN MISMATCH: BE `PUT /e-invoice-config` expect nested shape
   *   `{ provider, environment, seller_info: { company_name, tax_code, ... },
   *      defaults: { ... }, misa_credentials: { ... } }` (snake_case
   *   nested per ADR-013).
   *
   * FE onboarding step capture chỉ 2 field flat (taxCode, templateCode) →
   * shape không khớp DTO → BE 400. User vẫn nhấn "Hoàn tất" được vì caller
   * (invoice.tsx) wrap trong try/catch + skip lỗi.
   *
   * TODO follow-up: hoặc (a) expand UI form theo BE shape, hoặc (b) skip
   * call hoàn toàn ở onboarding (defer config sang Settings → Tài chính).
   * Hiện tại payload PUT sẽ fail mà không block UI.
   */
  async updateEInvoiceConfig(payload: EInvoiceConfigPayload) {
    const providerMap: Record<EInvoiceConfigPayload['provider'], string> = {
      meinvoice: 'MISA',
      vnpt: 'VNPT',
      viettel: 'Viettel',
      skip: 'MISA',
    };
    const { data: raw } = await api.put('/e-invoice-config', {
      provider: providerMap[payload.provider],
      active_environment: 'sandbox',
      seller_info: {
        company_name: payload.companyName,
        tax_code: payload.taxCode,
        address: payload.address,
        series_prefix: payload.templateCode?.slice(0, 2).toUpperCase(),
      },
      defaults: {
        auto_issue: false,
        auto_send_email: true,
        default_vat_rate: 10,
      },
    });
    return unwrap(raw);
  },
};
